import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Card, Alert, Dropdown, Badge, Button, Modal, Form, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import swal from 'sweetalert';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { stockService } from '../../../services/StockService';
import { successToastr, errorToastr } from '../../../../Utils/ToastrMessage';
import StockPaymentModal from './StockPaymentModal';
import SerialRangeInput from '../SerialManagement/SerialRangeInput';

// Enregistrer les composants Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

class StockDashboard extends Component {
    constructor(props) {
        super(props);
        this._isComponentMounted = false;
        this.state = {
            loading: true,
            dashboard: {
                totalStockItems: 0,
                totalStockValue: 0,
                lowStockItemsCount: 0,
                criticalStockItemsCount: 0,
                expiringSoonCount: 0,
                expiredItemsCount: 0,
                activeAlertsCount: 0,
                recentAlerts: [],
                topLowStockItems: [],
                stockByType: {},
                valueByType: {},
                consumptionTrends: []
            },
            stockItems: [],
            alerts: [],
            showAlertModal: false,
            showAddStockModal: false,
            showRestockModal: false,
            selectedStock: null,
            searchTerm: '',
            sortBy: 'productName',
            sortDirection: 'asc',
            filterType: 'all',
            newStock: {
                productCode: '',
                productName: '',
                stockType: '',
                initialQuantity: 0,
                minimumThreshold: 0,
                alertThreshold: 0,
                unitCost: 0.00,
                supplier: '',
                description: ''
            },
            restockData: {
                quantity: 0,
                unitCost: 0.00,
                supplier: '',
                notes: ''
            },
            showPaymentModal: false,
            selectedStockForPayment: null,
            // Gestion des plages de numéros de série
            serialRanges: [],
            requiresSerialRanges: false
        };
    }

    async componentDidMount() {
        this._isComponentMounted = true;
        await this.loadDashboardData();
        await this.loadStockItems();
        await this.loadAlerts();
    }

    componentWillUnmount() {
        this._isComponentMounted = false;
    }

    loadDashboardData = async () => {
        try {
            const dashboard = await stockService.getDashboardData();
            if (this._isComponentMounted) {
                this.setState({ dashboard });
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Error loading dashboard:', error);
            }

            // Charger des données fictives en cas d'erreur backend
            const mockDashboard = {
                totalStockItems: 0,
                totalStockValue: 0,
                lowStockItemsCount: 0,
                criticalStockItemsCount: 0,
                expiringSoonCount: 0,
                expiredItemsCount: 0,
                activeAlertsCount: 0,
                recentAlerts: [],
                topLowStockItems: [],
                stockByType: {},
                valueByType: {},
                consumptionTrends: []
            };

            if (this._isComponentMounted) {
                this.setState({ dashboard: mockDashboard });
                if (process.env.NODE_ENV === 'development') {
                    errorToastr('Service temporairement indisponible. Configuration requise.');
                }
            }
        }
    };

    loadStockItems = async () => {
        try {
            const stockItems = await stockService.getStockSummary();
            if (this._isComponentMounted) {
                this.setState({ stockItems, loading: false });
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Error loading stock items:', error);
            }

            // Charger des données vides en cas d'erreur
            if (this._isComponentMounted) {
                this.setState({ stockItems: [], loading: false });
                // En production, ne pas afficher de message d'erreur technique
            }
        }
    };

    loadAlerts = async () => {
        try {
            const alerts = await stockService.getActiveAlerts();
            if (this._isComponentMounted) {
                this.setState({ alerts });
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Error loading alerts:', error);
            }

            // Charger des données vides en cas d'erreur
            if (this._isComponentMounted) {
                this.setState({ alerts: [] });
            }
        }
    };

    handleSearch = (event) => {
        this.setState({ searchTerm: event.target.value });
    };

    handleSort = (field) => {
        const { sortBy, sortDirection } = this.state;
        const newDirection = sortBy === field && sortDirection === 'asc' ? 'desc' : 'asc';
        this.setState({ sortBy: field, sortDirection: newDirection });
    };

    handleFilterType = (type) => {
        this.setState({ filterType: type });
    };

    getFilteredAndSortedItems = () => {
        const { stockItems, searchTerm, sortBy, sortDirection, filterType } = this.state;

        // Vérification de sécurité - s'assurer que stockItems est un tableau
        if (!Array.isArray(stockItems)) {
            return [];
        }

        let filtered = stockItems.filter(item => {
            const matchesSearch = item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                item.productCode.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesFilter = filterType === 'all' ||
                                (filterType === 'low' && item.isLowStock) ||
                                (filterType === 'critical' && item.currentQuantity === 0) ||
                                (filterType === 'expiring' && item.isExpiringSoon);

            return matchesSearch && matchesFilter;
        });

        filtered.sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }

            if (sortDirection === 'asc') {
                return aVal < bVal ? -1 : 1;
            } else {
                return aVal > bVal ? -1 : 1;
            }
        });

        return filtered;
    };

    // Vérifier si le type de stock nécessite des plages de numéros de série
    requiresSerialRanges = (stockType) => {
        return stockType === 'BLANK_CARDS';
    };

    handleAddStock = async () => {
        try {
            // Validation de base
            if (!this.state.newStock.stockType) {
                errorToastr('Veuillez sélectionner un type de stock');
                return;
            }

            if (!this.state.newStock.productCode.trim()) {
                errorToastr('Le code produit est obligatoire');
                return;
            }

            if (!this.state.newStock.productName.trim()) {
                errorToastr('Le nom du produit est obligatoire');
                return;
            }

            if (this.state.newStock.initialQuantity <= 0) {
                errorToastr('La quantité initiale doit être supérieure à 0');
                return;
            }

            // Validation des plages pour les cartes blanches
            if (this.requiresSerialRanges(this.state.newStock.stockType)) {
                if (this.state.serialRanges.length === 0) {
                    errorToastr('Vous devez créer au moins une plage de numéros de série pour les cartes blanches');
                    return;
                }

                // Valider que la quantité totale des plages correspond à la quantité initiale
                const totalRangeQuantity = this.state.serialRanges.reduce((sum, range) =>
                    sum + (parseInt(range.endSerial) - parseInt(range.startSerial) + 1), 0);

                if (totalRangeQuantity !== this.state.newStock.initialQuantity) {
                    errorToastr(`La quantité totale des plages (${totalRangeQuantity}) doit correspondre à la quantité initiale (${this.state.newStock.initialQuantity})`);
                    return;
                }
            }

            // Créer le stock avec les plages si nécessaire
            const stockData = {
                ...this.state.newStock,
                serialRanges: this.requiresSerialRanges(this.state.newStock.stockType) ? this.state.serialRanges : []
            };

            await stockService.createStock(stockData);
            successToastr('Stock créé avec succès' + (this.state.serialRanges.length > 0 ? ` avec ${this.state.serialRanges.length} plage(s) de numéros` : ''));

            // Réinitialiser le formulaire
            this.setState({
                showAddStockModal: false,
                serialRanges: [],
                requiresSerialRanges: false,
                newStock: {
                    productCode: '',
                    productName: '',
                    stockType: '',
                    initialQuantity: 0,
                    minimumThreshold: 0,
                    alertThreshold: 0,
                    unitCost: 0.00,
                    supplier: '',
                    description: ''
                }
            });

            await this.loadStockItems();
            await this.loadDashboardData();
        } catch (error) {
            console.error('Error creating stock:', error);
            errorToastr('Erreur lors de la création du stock');
        }
    };

    // Callback quand une plage de numéros de série est créée
    handleRangeCreated = (newRange) => {
        this.setState(prevState => ({
            serialRanges: [...prevState.serialRanges, newRange]
        }));
        successToastr(`Plage ${newRange.startSerial}-${newRange.endSerial} ajoutée avec succès`);
    };

    // Callback quand une plage de numéros de série est mise à jour
    handleRangeUpdated = (updatedRange) => {
        this.setState(prevState => ({
            serialRanges: prevState.serialRanges.map(range =>
                range.id === updatedRange.id ? updatedRange : range
            )
        }));
        successToastr('Plage mise à jour avec succès');
    };

    // Gérer le changement de type de stock pour afficher/masquer les plages
    handleStockTypeChange = (e) => {
        const stockType = e.target.value;
        const requiresRanges = this.requiresSerialRanges(stockType);

        this.setState({
            newStock: { ...this.state.newStock, stockType },
            requiresSerialRanges: requiresRanges,
            serialRanges: requiresRanges ? this.state.serialRanges : [] // Réinitialiser si pas nécessaire
        });
    };

    handleRestock = async () => {
        try {
            // Validation des plages pour les cartes blanches en réapprovisionnement
            if (this.state.selectedStock && this.requiresSerialRanges(this.state.selectedStock.stockType)) {
                if (this.state.serialRanges.length === 0) {
                    errorToastr('Vous devez créer au moins une plage de numéros de série pour le réapprovisionnement des cartes blanches');
                    return;
                }

                // Valider que la quantité totale des plages correspond à la quantité à ajouter
                const totalRangeQuantity = this.state.serialRanges.reduce((sum, range) =>
                    sum + (parseInt(range.endSerial) - parseInt(range.startSerial) + 1), 0);

                if (totalRangeQuantity !== this.state.restockData.quantity) {
                    errorToastr(`La quantité totale des plages (${totalRangeQuantity}) doit correspondre à la quantité à ajouter (${this.state.restockData.quantity})`);
                    return;
                }
            }

            // Réapprovisionner avec les plages si nécessaire
            const restockDataWithRanges = {
                ...this.state.restockData,
                serialRanges: this.state.selectedStock && this.requiresSerialRanges(this.state.selectedStock.stockType)
                    ? this.state.serialRanges : []
            };

            await stockService.restock(this.state.selectedStock.id, restockDataWithRanges);
            successToastr('Réapprovisionnement effectué avec succès' + (this.state.serialRanges.length > 0 ? ` avec ${this.state.serialRanges.length} nouvelle(s) plage(s)` : ''));

            this.setState({
                showRestockModal: false,
                selectedStock: null,
                serialRanges: [],
                requiresSerialRanges: false,
                restockData: {
                    quantity: 0,
                    unitCost: 0.00,
                    supplier: '',
                    notes: ''
                }
            });

            await this.loadStockItems();
            await this.loadDashboardData();
        } catch (error) {
            console.error('Error restocking:', error);
            errorToastr('Erreur lors du réapprovisionnement');
        }
    };

    // Méthode pour ouvrir le modal de réapprovisionnement avec initialisation des plages si nécessaire
    openRestockModal = (stock) => {
        const requiresRanges = this.requiresSerialRanges(stock.stockType);
        this.setState({
            selectedStock: stock,
            showRestockModal: true,
            requiresSerialRanges: requiresRanges,
            serialRanges: []
        });
    };

    acknowledgeAlert = async (alertId) => {
        try {
            await stockService.acknowledgeAlert(alertId, 'CURRENT_USER'); // TODO: Get actual user ID
            successToastr('Alerte acquittée');
            await this.loadAlerts();
            await this.loadDashboardData();
        } catch (error) {
            console.error('Error acknowledging alert:', error);
            errorToastr('Erreur lors de l\'acquittement');
        }
    };

    handleDeleteStock = async (item) => {
        // Confirmation avec SweetAlert
        swal({
            title: `Supprimer l'article "${item.productName}" ?`,
            text: `Êtes-vous sûr de vouloir supprimer l'article "${item.productName}" (${item.productCode}) ? Cette action est irréversible.`,
            icon: "warning",
            buttons: {
                cancel: "Annuler",
                confirm: {
                    text: "Supprimer",
                    value: true,
                    visible: true,
                    closeModal: false
                }
            },
            dangerMode: true,
        }).then(async (willDelete) => {
            if (willDelete) {
                // Demander la raison de la suppression
                swal({
                    title: "Raison de la suppression",
                    text: "Veuillez indiquer la raison de la suppression:",
                    content: {
                        element: "input",
                        attributes: {
                            placeholder: "Suppression par l'administrateur",
                            type: "text",
                            value: "Suppression par l'administrateur"
                        }
                    },
                    buttons: {
                        cancel: "Annuler",
                        confirm: "Confirmer la suppression"
                    }
                }).then(async (reason) => {
                    if (reason !== null) {
                        try {
                            const finalReason = reason || 'Suppression par l\'administrateur';
                            await stockService.deactivateStock(item.id, finalReason, 'CURRENT_USER');
                            swal("Supprimé!", `Article "${item.productName}" supprimé avec succès`, "success");
                            await this.loadStockItems();
                            await this.loadDashboardData();
                        } catch (error) {
                            console.error('Error deleting stock:', error);
                            swal("Erreur", "Erreur lors de la suppression de l'article", "error");
                        }
                    }
                });
            }
        });
    };

    // PrimeReact DataTable body templates
    quantityBodyTemplate = (rowData) => {
        return (
            <div>
                <span className={rowData.currentQuantity === 0 ? 'text-danger' : rowData.isLowStock ? 'text-warning' : ''}>
                    {rowData.currentQuantity}
                </span>
                <small className="text-muted"> / {rowData.minimumThreshold}</small>
            </div>
        );
    };

    costBodyTemplate = (rowData) => {
        return `${rowData.unitCost.toLocaleString('fr-FR')} FCFA`;
    };

    statusBodyTemplate = (rowData) => {
        const badgeVariant = rowData.currentQuantity === 0 ? 'danger' :
                            rowData.isLowStock ? 'warning' :
                            rowData.isExpiringSoon ? 'info' : 'success';

        return (
            <Badge variant={badgeVariant}>
                {rowData.statusDisplay}
            </Badge>
        );
    };

    actionsBodyTemplate = (rowData) => {
        return (
            <div className="d-flex gap-1">
                <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => this.openRestockModal(rowData)}
                    title="Réapprovisionner"
                    style={{ padding: '4px 8px' }}
                >
                    <i className="fa fa-plus"></i>
                </Button>
                <Button
                    size="sm"
                    variant="outline-success"
                    onClick={() => this.setState({ selectedStockForPayment: rowData, showPaymentModal: true })}
                    title="Paiement"
                    style={{ padding: '4px 8px' }}
                >
                    <i className="fa fa-credit-card"></i>
                </Button>
                <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => this.handleDeleteStock(rowData)}
                    title="Supprimer"
                    style={{ padding: '4px 8px' }}
                >
                    <i className="fa fa-trash"></i>
                </Button>
            </div>
        );
    };

    render() {
        const { dashboard, stockItems, alerts, loading } = this.state;
        const filteredItems = this.getFilteredAndSortedItems();

        // Configuration des graphiques avec vérifications de sécurité
        const stockByTypeChartData = {
            labels: dashboard.stockByType && typeof dashboard.stockByType === 'object' ? Object.keys(dashboard.stockByType) : [],
            datasets: [{
                data: dashboard.stockByType && typeof dashboard.stockByType === 'object' ? Object.values(dashboard.stockByType) : [],
                backgroundColor: [
                    '#007bff',
                    '#28a745',
                    '#ffc107',
                    '#dc3545',
                    '#17a2b8',
                    '#6f42c1'
                ]
            }]
        };

        const consumptionTrendData = {
            labels: Array.isArray(dashboard.consumptionTrends) ? dashboard.consumptionTrends.map(t => t.period) : [],
            datasets: [{
                label: 'Consommation',
                data: Array.isArray(dashboard.consumptionTrends) ? dashboard.consumptionTrends.map(t => t.totalConsumed) : [],
                borderColor: '#007bff',
                backgroundColor: 'rgba(0, 123, 255, 0.1)',
                tension: 0.4
            }]
        };

        if (loading) {
            return (
                <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Chargement...</span>
                    </div>
                </div>
            );
        }

        return (
            <div className="container-fluid">
                <div className="row page-titles mx-0">
                    <div className="col-sm-6 p-md-0">
                        <div className="welcome-text">
                            <h4>Gestion des Stocks</h4>
                            <span className="ml-1">Tableau de bord et gestion des stocks de cartes</span>
                        </div>
                    </div>
                    <div className="col-sm-6 p-md-0 justify-content-sm-end mt-2 mt-sm-0 d-flex">
                        <Link to="/cartes/gestion-complete" className="btn btn-info mr-2">
                            <i className="fa fa-cog mr-2"></i>
                            Gestion Complète des Cartes
                        </Link>
                        <Link to="/cartes-production" className="btn btn-success mr-2">
                            <i className="fa fa-print mr-2"></i>
                            Production
                        </Link>
                        <Button variant="primary" onClick={() => this.setState({ showAddStockModal: true })}>
                            <i className="fa fa-plus mr-2"></i>
                            Nouveau Stock
                        </Button>
                    </div>
                </div>

                {/* Message d'information si pas de données - uniquement en développement */}
                {process.env.NODE_ENV === 'development' && (dashboard.totalStockItems || 0) === 0 && stockItems.length === 0 && (
                    <Row className="mb-4">
                        <Col xl={12}>
                            <Alert variant="info">
                                <Alert.Heading>ℹ️ Module en cours de configuration</Alert.Heading>
                                <p>
                                    Le module de gestion des stocks est en cours d'initialisation.
                                </p>
                                <hr />
                                <p className="mb-0">
                                    <strong>Pour les développeurs :</strong> Exécutez le script de migration :
                                    <br />
                                    <code>Interface_Admin/setup-stock-database.ps1</code>
                                </p>
                            </Alert>
                        </Col>
                    </Row>
                )}

                {/* Statistiques principales */}
                <Row className="mb-4">
                    <Col xl={3} xxl={3} sm={6}>
                        <Card className="bg-warning text-white">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <i className="la la-boxes la-3x mr-3"></i>
                                    <div>
                                        <h4 className="mb-0 text-white">{dashboard.totalStockItems || 0}</h4>
                                        <span>Articles en stock</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={3} xxl={3} sm={6}>
                        <Card className="bg-success text-white">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <i className="la la-dollar-sign la-3x mr-3"></i>
                                    <div>
                                        <h4 className="mb-0 text-white">{(dashboard.totalStockValue || 0).toLocaleString('fr-FR')} FCFA</h4>
                                        <span>Valeur totale</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={3} xxl={3} sm={6}>
                        <Card className="bg-danger text-white">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <i className="la la-exclamation-triangle la-3x mr-3"></i>
                                    <div>
                                        <h4 className="mb-0 text-white">{dashboard.lowStockItemsCount || 0}</h4>
                                        <span>Stocks faibles</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={3} xxl={3} sm={6}>
                        <Card className="bg-info text-white">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <i className="la la-bell la-3x mr-3"></i>
                                    <div>
                                        <h4 className="mb-0 text-white">{dashboard.activeAlertsCount || 0}</h4>
                                        <span>Alertes actives</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    {/* Graphiques */}
                    <Col xl={6}>
                        <Card>
                            <Card.Header>
                                <Card.Title>Répartition par Type</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Doughnut
                                    data={stockByTypeChartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false
                                    }}
                                    height={300}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={6}>
                        <Card>
                            <Card.Header>
                                <Card.Title>Tendances de Consommation</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Line
                                    data={consumptionTrendData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false
                                    }}
                                    height={300}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Alertes récentes */}
                {alerts.length > 0 && (
                    <Row className="mb-4">
                        <Col xl={12}>
                            <Card>
                                <Card.Header>
                                    <Card.Title>Alertes Actives ({alerts.length})</Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    {alerts.slice(0, 5).map(alert => (
                                        <Alert
                                            key={alert.id}
                                            variant={alert.priority === 'Critical' ? 'danger' : alert.priority === 'High' ? 'warning' : 'info'}
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <strong>{alert.alertTypeDisplay}</strong> - {alert.message}
                                                <br />
                                                <small>Créé le {new Date(alert.createdDate).toLocaleString('fr-FR')}</small>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => this.acknowledgeAlert(alert.id)}
                                            >
                                                Acquitter
                                            </Button>
                                        </Alert>
                                    ))}
                                    {alerts.length > 5 && (
                                        <div className="text-center">
                                            <Link to="/cartes-stock/alerts" className="btn btn-outline-primary">
                                                Voir toutes les alertes ({alerts.length})
                                            </Link>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Liste des stocks */}
                <Row>
                    <Col xl={12}>
                        <Card>
                            <Card.Header>
                                <Card.Title>Inventaire des Stocks</Card.Title>
                                <div className="card-action">
                                    <Row>
                                        <Col md={4}>
                                            <InputGroup>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Rechercher..."
                                                    value={this.state.searchTerm}
                                                    onChange={this.handleSearch}
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Control
                                                as="select"
                                                value={this.state.filterType}
                                                onChange={(e) => this.handleFilterType(e.target.value)}
                                            >
                                                <option value="all">Tous les stocks</option>
                                                <option value="low">Stocks faibles</option>
                                                <option value="critical">Stocks critiques</option>
                                                <option value="expiring">Expiration proche</option>
                                            </Form.Control>
                                        </Col>
                                    </Row>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <DataTable
                                    value={filteredItems}
                                    paginator
                                    rows={10}
                                    loading={loading}
                                    stripedRows
                                    sortMode="single"
                                    emptyMessage="Aucun article en stock"
                                    className="p-datatable-sm"
                                    responsiveLayout="scroll"
                                    size="small"
                                >
                                    <Column
                                        field="productCode"
                                        header="Code Produit"
                                        sortable
                                        style={{ width: '120px' }}
                                    />
                                    <Column
                                        field="productName"
                                        header="Nom Produit"
                                        sortable
                                        style={{ minWidth: '200px' }}
                                    />
                                    <Column
                                        field="stockType"
                                        header="Type"
                                        sortable
                                        style={{ width: '120px' }}
                                    />
                                    <Column
                                        field="currentQuantity"
                                        header="Quantité"
                                        body={this.quantityBodyTemplate}
                                        sortable
                                        style={{ width: '120px' }}
                                    />
                                    <Column
                                        field="unitCost"
                                        header="Coût Unitaire"
                                        body={this.costBodyTemplate}
                                        sortable
                                        style={{ width: '130px' }}
                                    />
                                    <Column
                                        field="statusDisplay"
                                        header="Statut"
                                        body={this.statusBodyTemplate}
                                        style={{ width: '100px' }}
                                    />
                                    <Column
                                        header="Actions"
                                        body={this.actionsBodyTemplate}
                                        style={{ width: '100px', textAlign: 'center' }}
                                    />
                                </DataTable>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Modal Ajout Stock */}
                <Modal show={this.state.showAddStockModal} onHide={() => this.setState({ showAddStockModal: false })} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Créer un nouveau stock</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Code Produit *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={this.state.newStock.productCode}
                                            onChange={(e) => this.setState({
                                                newStock: { ...this.state.newStock, productCode: e.target.value }
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Type de Stock *</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={this.state.newStock.stockType}
                                            onChange={this.handleStockTypeChange}
                                        >
                                            <option value="">-- Sélectionner le type de stock --</option>
                                            <option value="BLANK_CARDS">Cartes Blanches</option>
                                            <option value="INK_CARTRIDGE">Cartouches d'Encre</option>
                                            <option value="RIBBON">Rubans d'Impression</option>
                                            <option value="PACKAGING_MATERIAL">Matériaux d'Emballage</option>
                                            <option value="LAMINATING_FILM">Film de Laminage</option>
                                            <option value="OTHER">Autre</option>
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group>
                                <Form.Label>Nom du Produit *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={this.state.newStock.productName}
                                    onChange={(e) => this.setState({
                                        newStock: { ...this.state.newStock, productName: e.target.value }
                                    })}
                                />
                            </Form.Group>
                            <Row>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Quantité Initiale *</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={this.state.newStock.initialQuantity}
                                            onChange={(e) => this.setState({
                                                newStock: { ...this.state.newStock, initialQuantity: parseInt(e.target.value) || 0 }
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Seuil Minimum *</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={this.state.newStock.minimumThreshold}
                                            onChange={(e) => this.setState({
                                                newStock: { ...this.state.newStock, minimumThreshold: parseInt(e.target.value) || 0 }
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Seuil d'Alerte *</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={this.state.newStock.alertThreshold}
                                            onChange={(e) => this.setState({
                                                newStock: { ...this.state.newStock, alertThreshold: parseInt(e.target.value) || 0 }
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Coût Unitaire (FCFA) *</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={this.state.newStock.unitCost}
                                            onChange={(e) => this.setState({
                                                newStock: { ...this.state.newStock, unitCost: parseFloat(e.target.value) || 0 }
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Fournisseur</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={this.state.newStock.supplier}
                                            onChange={(e) => this.setState({
                                                newStock: { ...this.state.newStock, supplier: e.target.value }
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group>
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={this.state.newStock.description}
                                    onChange={(e) => this.setState({
                                        newStock: { ...this.state.newStock, description: e.target.value }
                                    })}
                                />
                            </Form.Group>

                            {/* Gestion des plages de numéros de série pour les cartes blanches */}
                            {this.state.requiresSerialRanges && (
                                <div className="mt-4">
                                    <Alert variant="info">
                                        <strong>Plages de numéros de série requises</strong><br />
                                        Pour les cartes blanches, vous devez créer des plages de numéros de série.
                                        La quantité totale des plages doit correspondre à la quantité initiale ({this.state.newStock.initialQuantity}).
                                    </Alert>

                                    <SerialRangeInput
                                        cardStockId={null} // Pas encore créé
                                        onRangeCreated={this.handleRangeCreated}
                                        onRangeUpdated={this.handleRangeUpdated}
                                        existingRanges={this.state.serialRanges}
                                        showCreateForm={true}
                                        isEmbedded={true}
                                    />

                                    {this.state.serialRanges.length > 0 && (
                                        <div className="mt-3">
                                            <h6>Plages créées :</h6>
                                            <ul className="list-group">
                                                {this.state.serialRanges.map((range, index) => (
                                                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                                        <span>
                                                            {range.startSerial} - {range.endSerial}
                                                            <small className="text-muted ms-2">
                                                                ({parseInt(range.endSerial) - parseInt(range.startSerial) + 1} cartes)
                                                            </small>
                                                        </span>
                                                        <Badge bg="primary" pill>
                                                            {range.serialFormat}
                                                        </Badge>
                                                    </li>
                                                ))}
                                            </ul>
                                            <small className="text-success mt-2 d-block">
                                                Total: {this.state.serialRanges.reduce((sum, range) =>
                                                    sum + (parseInt(range.endSerial) - parseInt(range.startSerial) + 1), 0)} cartes
                                            </small>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.setState({ showAddStockModal: false })}>
                            Annuler
                        </Button>
                        <Button variant="primary" onClick={this.handleAddStock}>
                            Créer le Stock
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal Réapprovisionnement */}
                <Modal show={this.state.showRestockModal} onHide={() => this.setState({ showRestockModal: false })}>
                    <Modal.Header closeButton>
                        <Modal.Title>Réapprovisionnement</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {this.state.selectedStock && (
                            <>
                                <Alert variant="info">
                                    <strong>{this.state.selectedStock.productName}</strong><br />
                                    Stock actuel: {this.state.selectedStock.currentQuantity}<br />
                                    Seuil minimum: {this.state.selectedStock.minimumThreshold}
                                </Alert>
                                <Form>
                                    <Form.Group>
                                        <Form.Label>Quantité à ajouter *</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={this.state.restockData.quantity}
                                            onChange={(e) => this.setState({
                                                restockData: { ...this.state.restockData, quantity: parseInt(e.target.value) || 0 }
                                            })}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label>Coût unitaire (FCFA)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={this.state.restockData.unitCost}
                                            onChange={(e) => this.setState({
                                                restockData: { ...this.state.restockData, unitCost: parseFloat(e.target.value) || 0 }
                                            })}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label>Fournisseur</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={this.state.restockData.supplier}
                                            onChange={(e) => this.setState({
                                                restockData: { ...this.state.restockData, supplier: e.target.value }
                                            })}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label>Notes</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={this.state.restockData.notes}
                                            onChange={(e) => this.setState({
                                                restockData: { ...this.state.restockData, notes: e.target.value }
                                            })}
                                        />
                                    </Form.Group>

                                    {/* Gestion des plages de numéros de série pour réapprovisionnement des cartes blanches */}
                                    {this.state.requiresSerialRanges && (
                                        <div className="mt-4">
                                            <Alert variant="warning">
                                                <strong>Plages de numéros de série requises</strong><br />
                                                Pour le réapprovisionnement des cartes blanches, vous devez créer de nouvelles plages de numéros de série.
                                                La quantité totale des plages doit correspondre à la quantité à ajouter ({this.state.restockData.quantity}).
                                            </Alert>

                                            <SerialRangeInput
                                                cardStockId={this.state.selectedStock?.id}
                                                onRangeCreated={this.handleRangeCreated}
                                                onRangeUpdated={this.handleRangeUpdated}
                                                existingRanges={this.state.serialRanges}
                                                showCreateForm={true}
                                                isEmbedded={true}
                                            />

                                            {this.state.serialRanges.length > 0 && (
                                                <div className="mt-3">
                                                    <h6>Nouvelles plages créées :</h6>
                                                    <ul className="list-group">
                                                        {this.state.serialRanges.map((range, index) => (
                                                            <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                                                <span>
                                                                    {range.startSerial} - {range.endSerial}
                                                                    <small className="text-muted ms-2">
                                                                        ({parseInt(range.endSerial) - parseInt(range.startSerial) + 1} cartes)
                                                                    </small>
                                                                </span>
                                                                <Badge bg="success" pill>
                                                                    {range.serialFormat}
                                                                </Badge>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <small className="text-success mt-2 d-block">
                                                        Total nouvelles cartes: {this.state.serialRanges.reduce((sum, range) =>
                                                            sum + (parseInt(range.endSerial) - parseInt(range.startSerial) + 1), 0)}
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Form>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.setState({ showRestockModal: false })}>
                            Annuler
                        </Button>
                        <Button variant="primary" onClick={this.handleRestock}>
                            Réapprovisionner
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Stock Payment Modal */}
                <StockPaymentModal
                    isOpen={this.state.showPaymentModal}
                    onClose={() => this.setState({ showPaymentModal: false, selectedStockForPayment: null })}
                    stockItem={this.state.selectedStockForPayment}
                    onPaymentSuccess={(result) => {
                        console.log('Payment successful:', result);
                        successToastr('Paiement traité avec succès');
                        // Refresh stock data after payment
                        this.loadStockItems();
                    }}
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(StockDashboard);